import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { http } from "../../lib/api";

const Row = ({ label, value, id }) => {
  const copy = () => {
    navigator.clipboard?.writeText(value);
    toast.success(`${label} copied`);
  };
  return (
    <div className="flex items-center justify-between gap-4 border-b border-white/10 py-4 last:border-0">
      <div className="min-w-0">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--sage)]">{label}</p>
        <p className="truncate font-display text-xl font-bold sm:text-2xl" data-testid={`bank-${id}`}>{value}</p>
      </div>
      <button onClick={copy} className="qty-btn shrink-0" aria-label={`Copy ${label}`} data-testid={`bank-copy-${id}`}><Copy className="h-4 w-4" /></button>
    </div>
  );
};

const BankRows = () => {
  const [bank, setBank] = useState(null);
  useEffect(() => {
    http.get("/settings/payment").then((r) => setBank(r.data ?? null)).catch(() => setBank(null));
  }, []);
  if (!bank) return <p className="py-4 text-sm text-[var(--sage)]" data-testid="bank-loading">Bank details are also in your confirmation email.</p>;
  return (
    <>
      <Row label="Account number" value={bank.account_number ?? ""} id="account-number" />
      <Row label="Account name" value={bank.account_name ?? ""} id="account-name" />
      <Row label="Bank" value={bank.bank ?? ""} id="bank-name" />
    </>
  );
};

export const Confirmation = ({ order, onNew }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} data-testid="order-confirmation">
    <div className="flex items-center gap-3">
      <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.2 }} className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--leaf)] text-white"><Check className="h-6 w-6" /></motion.span>
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--leaf)]">Order received</p>
    </div>
    <h3 className="mt-6 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Thanks, {order?.name?.split(" ")[0] ?? "friend"}!</h3>
    <p className="mt-4 text-[var(--sage)]">
      To confirm your order, transfer <span className="font-semibold text-white" data-testid="confirmation-total">${Number(order?.total ?? 0).toFixed(2)}</span> by online banking using the reference below. We'll start packing once it arrives. We've also emailed you these details.
    </p>
    {order?.subscription_ref && (
      <p className="mt-4 rounded-2xl border border-[#15803D]/30 bg-[#DCFCE7] px-4 py-3 text-sm" data-testid="confirmation-subscription">
        You're subscribed to a box <strong>{order.frequency === "weekly" ? "every week" : "every fortnight"}</strong> ({order.subscription_ref}). We'll email you a new reference to pay before each delivery.
      </p>
    )}
    <div className="mt-8 rounded-3xl border border-[#EAB308]/50 bg-[#FEF9E7] px-6 py-2">
      <Row label="Reference" value={order?.ref ?? ""} id="ref" />
      <BankRows />
    </div>
    <button onClick={onNew} className="link-underline mt-8 text-sm text-[var(--sage)] hover:text-white" data-testid="order-new-button">Place another order</button>
  </motion.div>
);
